import { type Ref, ref, onUnmounted } from 'vue';

/**
 * 监听 DOM 元素宽高变化，返回响应式宽高
 * @param targetRef - DOM 元素的 ref 引用
 * @returns { width: Ref<number>, height: Ref<number> } 响应式宽高
 */
export const useElementSize = (targetRef: Ref<HTMLElement | null>) => {
  const width = ref<number>(0);
  const height = ref<number>(0);
  let resizeObserver: ResizeObserver | null = null;

  /** 从 DOM 读取当前宽高写入响应式值（步骤 1：空元素跳过；步骤 2：写 offset 尺寸） */
  const remeasure = () => {
    if (!targetRef.value) return;
    width.value = targetRef.value.offsetWidth;
    height.value = targetRef.value.offsetHeight;
  };

  // 监听变化
  const startObserve = (callback?: () => void) => {
    if (!targetRef.value) return;

    resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      width.value = entry.contentRect.width;
      height.value = entry.contentRect.height;

      if (callback) callback();
    });

    resizeObserver.observe(targetRef.value);
    remeasure();
  };

  // 停止监听
  const stopObserve = () => {
    if (resizeObserver) {
      if (targetRef.value) resizeObserver.unobserve(targetRef.value);

      resizeObserver.disconnect();

      resizeObserver = null;
    }
  };

  // 组件卸载时停止监听
  onUnmounted(stopObserve);

  return { width, height, startObserve, stopObserve, remeasure };
};
